import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail, KeyRound, Eye, EyeOff, Check } from 'lucide-react';
import { useRecaptcha } from '../hooks/useRecaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ForgotPasswordPage = () => {
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'done'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const captcha = useRecaptcha();

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (captcha.enabled && !captcha.token) {
      toast.error('Please complete the captcha challenge');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, recaptcha_token: captcha.token })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Reset code sent! Check your email.');
        setStep('code');
      } else {
        toast.error(data.detail || 'Failed to send reset code');
        captcha.reset();
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
      captcha.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setStep('done');
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
          <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
          <span className="text-xl font-bold text-slate-900">Formlio</span>
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {step === 'email' && (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1" data-testid="forgot-password-heading">Forgot password?</h2>
                <p className="text-slate-500 text-sm">Enter your email and we'll send you a reset code</p>
              </div>
              <form onSubmit={handleSendCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl"
                    data-testid="forgot-email-input"
                  />
                </div>
                {captcha.enabled && (
                  <div className="pt-1" data-testid="forgot-captcha">
                    <div ref={captcha.containerRef} />
                  </div>
                )}
                <Button type="submit" disabled={loading || (captcha.enabled && !captcha.token)} className="w-full h-12 rounded-xl" data-testid="send-reset-code-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send Reset Code'}
                </Button>
              </form>
            </>
          )}

          {step === 'code' && (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-7 h-7 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">Reset your password</h2>
                <p className="text-slate-500 text-sm">Enter the code sent to <strong>{email}</strong></p>
              </div>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Reset Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    className="h-12 rounded-xl text-center text-lg tracking-widest font-mono"
                    maxLength={6}
                    data-testid="reset-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="h-12 rounded-xl pr-12"
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-12 rounded-xl"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl" data-testid="reset-password-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</> : 'Reset Password'}
                </Button>
                <button type="button" onClick={() => handleSendCode({ preventDefault: () => {} })} className="w-full text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                  Didn't receive the code? Resend
                </button>
              </form>
            </>
          )}

          {step === 'done' && (
            <div className="text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Password reset!</h2>
              <p className="text-slate-500 mb-6">Your password has been successfully changed.</p>
              <Button onClick={() => navigate('/login')} className="w-full h-12 rounded-xl" data-testid="go-to-login-btn">
                Go to Login
              </Button>
            </div>
          )}

          {step !== 'done' && (
            <Link to="/login" className="flex items-center justify-center gap-2 mt-6 text-sm text-slate-500 hover:text-slate-700 transition-colors" data-testid="back-to-login-link">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
